/**
 * @flow
 * @prettier
 */
/* eslint-disable camelcase */
import React from 'react';
import ReactDOM from 'react-dom';

import type { Classed } from '#common/utils/store';
import type { AnyStoreItemType } from '../../../utils/types';
import type { IStoreProps } from '../../templates/Store';
import type { Translations, ScriptCommonArgs } from '../../types';

import { isCustomAuth } from '../../../experiments/customAuth/utils';
import { ScriptsState } from '../../../reducers/types/scripts-state';
import { canShowCart, PlaceTypes } from '../../../utils/cart-helper';
import { documentClickListener } from '../../../utils/document-listener';
import {
  ALL_VC_TYPE_NAME,
  VI_TYPE,
  VC_TYPE,
  CUSTOM_AMOUNT,
  ERROR_VC_GROUP,
  BUNDLE_TYPE,
  GK_TYPE,
  generateDrmList,
} from '../../../utils/store-helper';
import { addToCart } from '../../../utils/store/add-to-cart';
import { getPrice } from '../../../utils/store/price-item';
import { updateHidingButtons } from '../../../utils/store/update-hiding-buttons';
import {
  CustomAmountLoaderService,
  onClickCustomAmount,
} from '../../actions/buyCustomAmount';
import { handleUniversalItem } from '../../actions/buyUniversalItem';
import { analyticsManager } from '../../afterBlocks/analytics/analytics';
import { AnalyticsActions } from '../../afterBlocks/analytics/analyticsActions';
import { AnalyticsCategories } from '../../afterBlocks/analytics/analyticsCategories';
import { lazyLoadImage } from '../../beforeBlocks/lazyLoad';
import events from '../../events';
import {
  getTokenFromCookie,
  showNotification,
  toggleElement,
} from '../../helpers/common';
import { getStore } from '../../store';
import Store from '../../templates/Store';
import { translations } from '../../translations';
import * as loader from '../loader';
import { openLoginWidget } from '../login';
import { buyItemForVirtualCurrency } from './api';
import { showMoreBundle, closeBundleInfoModal } from './bundle-modal';
import { UniversalCart } from './cart/UniversalCart';
import { initCartModalOnce } from './cartModal';
import { CustomAmountOnLanding } from './CustomAmountOnLanding';
import { StoreLoaderService } from './services/loader';

import { LandingType, AuthorizationType } from '#common/model/landing';
import { FiveSymbolLocale } from '#common/types/locale';
import { LandingTypes } from '#common/types/model/landing';
import { StoreItem, DEFAULT_VC_IMAGE } from '#common/utils/store';

const renderEmptyTemplate = (target, storeItemProps) => {
  ReactDOM.hydrate(<Store {...storeItemProps} />, target);
};

const renderTemplate = (target, storeItemPropsList) => {
  ReactDOM.hydrate(
    <>
      {storeItemPropsList.map((storeItemProps) => (
        <Store
          key={storeItemProps.itemSku}
          {...storeItemProps}
          closeBundleInfoModal={closeBundleInfoModal}
        />
      ))}
    </>,
    target
  );
};

const generateTemplate = ({
  templateDataList,
  currentSection,
  currentSectionList,
}) => {
  currentSection.getElementsByClassName('store__preloader')[0].remove();
  renderTemplate(currentSectionList, templateDataList);
  lazyLoadImage.update();
};

const canPurchase = (sku, inventory) => {
  const inventoryItem = inventory.getItem(sku);
  return (
    !inventoryItem ||
    inventoryItem.virtualItemType !== 'non_consumable' ||
    inventoryItem.quantity === 0
  );
};

const addListeners = ({ templateDataList, cart, inventory, userToken }) => {
  const cartModal = document.querySelector('[data-cart-modal]');
  templateDataList.forEach((templateData) => {
    const buyButtons = document.querySelectorAll(
      `#${templateData.buttonId}, #${templateData.buttonId}--modal`
    );
    [...buyButtons].forEach((buyButton: Element) => {
      if (cartModal && templateData.enableCart) {
        // $FlowFixMe
        const parentElement: Element = buyButton.parentNode;
        const addToCartButton =
          parentElement &&
          parentElement.querySelector('[data-button-type="add-to-cart"]');
        const checkoutButton =
          parentElement &&
          parentElement.querySelector('[data-button-type="cart-modal-opener"]');
        const updateButtonsProps = {
          addToCartButton,
          buyButton,
          checkoutButton,
          cart,
          sku: templateData.itemSku,
          storeAPIDataService: templateData.storeAPIDataService,
        };
        if (addToCartButton || checkoutButton) {
          cart.addListener(() => updateHidingButtons(updateButtonsProps));
          updateHidingButtons(updateButtonsProps);
        }
      }
      if (userToken) {
        const footer = buyButton.closest('.store__item-footer');
        if (footer) {
          const storeButtons = footer.querySelector(
            '[data-type="store-buttons"]'
          );
          const priceBlock = footer.querySelector('[data-type="price-block"]');
          const purchasedText = footer.querySelector(
            '[data-type="purchased-text"]'
          );
          const showPurchasedText = () => {
            const hiddenPurchasedText = canPurchase(
              templateData.itemSku,
              inventory
            );
            toggleElement(storeButtons, () => hiddenPurchasedText);
            toggleElement(priceBlock, () => hiddenPurchasedText);
            toggleElement(purchasedText, () => !hiddenPurchasedText);
          };
          inventory.addListener(() => showPurchasedText());
        }
      }
    });
  });
};

const isGameItem = (type: string) => type === GK_TYPE;

const buyForVirtualCurrency = ({ userToken }: { userToken?: string }) => {
  const store = getStore();
  const {
    landingServices: { userService },
    scripts: { projectId },
  } = store.getState();

  documentClickListener.addListener({
    selector: `.store__buy-btn[data-currency-type="${VC_TYPE}"]`,
    handle: async (element) => {
      if (!userToken) {
        throw Error('userToken is undefined');
      }
      element.classList.add('store__buy-btn--is-loading');
      const virtualItemsSku = element.getAttribute('data-sku');
      const virtualCurrencySku = element.getAttribute('data-vc');

      const result = await buyItemForVirtualCurrency(
        userToken,
        projectId,
        virtualItemsSku,
        virtualCurrencySku
      );
      element.classList.remove('store__buy-btn--is-loading');

      if (result.order_id) {
        showNotification(
          translations['client.store.successful_purchase'],
          'success'
        );
        userService.updateBalance();
      }

      if (!result.errorCode) {
        return;
      }

      switch (result.errorCode) {
        case 5006:
          showNotification(translations['client.store.not_enough_vc'], 'error');
          break;
        case 1422:
          showNotification(
            translations['client.store.non_consumable_item'],
            'error'
          );
          break;
        default:
          showNotification(translations['client.common_error'], 'error');
          break;
      }
    },
  });
};

const buyForRealPrice = ({
  isAuth,
  buttons,
  data,
  block,
  translations,
  storeAPIDataService,
}: {
  isAuth: boolean,
  buttons: HTMLElement[],
  data: ScriptsState,
  block: HTMLElement,
  translations: any,
  storeAPIDataService: any,
}) => {
  const selector = `.store__buy-btn[data-currency-type="real"]${
    !isAuth ? '[data-require-auth="false"]' : ''
  }`;
  documentClickListener.addListener({
    selector,
    handle: (button) => {
      analyticsManager.sendEvent({
        category: AnalyticsCategories.STORE,
        event: AnalyticsActions.CLICK_BUY,
        page: `landing_${AnalyticsActions.CLICK_BUY}`,
        label: button.getAttribute('data-sku'),
      });
      closeBundleInfoModal(button.getAttribute('data-sku'));
    },
  });

  buttons
    .filter((button) => {
      const type = button.getAttribute('data-currency-type');
      const isRequireAuth = button.getAttribute('data-require-auth');
      return type === 'real' && (isAuth || isRequireAuth === 'false');
    })
    .forEach((button: HTMLElement) => {
      const universalItem = {
        sku: button.getAttribute('data-sku'),
        target: `#${button.getAttribute('id') || ''}`,
        type: button.getAttribute('data-item-type'),
        isPreOrder: button.getAttribute('data-is-pre-order') === 'true',
      };

      handleUniversalItem({
        data,
        universalItem,
        block,
        translations,
        storeAPIDataService,
        requiresBuyButtonWidget: !isAuth,
      });
    });
};

const collectGeneratedData = ({
  item,
  itemSectionType,
  translations,
  groupId,
  sectionIndex,
  blockStructure,
  landingType,
  enableCart,
  locale,
  cart,
  storeAPIDataService,
  block,
}: {
  item: Classed<AnyStoreItemType>,
  itemSectionType: string,
  translations: Translations,
  groupId: string,
  sectionIndex: number,
  blockStructure: Object | null,
  landingType: LandingTypes,
  enableCart: boolean,
  locale: FiveSymbolLocale,
  cart: UniversalCart,
  storeAPIDataService: Object,
  block: HTMLElement,
}): Object => {
  const {
    sku,
    name,
    description,
    image_url: image,
    unit_items: drmList = [],
    content,
  } = item;

  const priceForStoreItem = getPrice(
    item,
    itemSectionType,
    translations['client.store.item.price_from']
  );

  const isBundleItem = StoreItem.isBundle(item);
  const isPreOrder = !!(drmList[0] && drmList[0].is_pre_order);

  const btnGroupId =
    groupId === ALL_VC_TYPE_NAME || isGameItem(itemSectionType)
      ? 'items'
      : groupId;

  const buttonId = `${
    block.id
  }-${btnGroupId}-${sku.toLocaleLowerCase()}-${sectionIndex}`.replace(
    /\./g,
    ''
  );

  const selectorList = generateDrmList(drmList) || [];
  const showDrmList = isGameItem(itemSectionType) && selectorList.length > 1;
  const cartSku = !isGameItem(itemSectionType)
    ? sku
    : !showDrmList && selectorList.length !== 0 && selectorList[0].sku;

  const buttonType = !isGameItem(itemSectionType)
    ? 'cart-button'
    : `cart-button-drm${!showDrmList ? '-one' : ''}`;

  const currentComponent = blockStructure?.components[sectionIndex];
  const descriptionEnable = currentComponent?.descriptionEnable;
  const imageSize = currentComponent?.imageSize;
  const cardSize = blockStructure?.values.cardSize;
  const showMoreEnable = isBundleItem && currentComponent?.moreInfoEnable;

  const commonData: IStoreProps = {
    name,
    description,
    image: image || DEFAULT_VC_IMAGE,
    id: `${block.id}-${sku}`,
    itemSku: sku,
    itemType: itemSectionType,
    price: priceForStoreItem,
    buttonId,
    isPreOrder,
    buttonText: isPreOrder
      ? translations['client.buy_button.pre-order']
      : translations['client.buy_button.text'],
    checkoutButtonText: translations['client.buy_button.checkout'],
    addCartButtonText: translations['client.store.cart.add_button'],
    showMoreButtonText: translations['client.store.item.show_more'],
    isForVc: false,
    requireAuth: item.isRequireAuth(),
    enableCart,
    cartSku,
    selectorList,
    showDrmList,
    buttonType,
    purchasedText: '',
    viType: '',
    itemGameClass: isGameItem(itemSectionType) ? 'for-game' : '',
    itemBundleClass: isBundleItem ? 'store__item-bundle' : '',
    xaElementValue: itemSectionType,
    xaExtra: JSON.stringify({
      name: sku,
      title: name,
      block: 'store',
    }),
    descriptionEnable,
    isTextNeeded: cardSize === 'small' || showMoreEnable,
    content,
    locale,
    imageSize,
    showMoreEnable,
    addToCart: addToCart({ cart, locale, storeAPIDataService, sku }),
  };

  let generatedData = commonData;
  if (priceForStoreItem.vcPrice && landingType !== LandingType.TOPUP) {
    generatedData = {
      ...generatedData,
      isForVc: true,
      requireAuth: true,
    };
  }

  if (itemSectionType === VI_TYPE) {
    generatedData.viType = item.virtual_item_type;
    generatedData.requireAuth = true;
    generatedData.purchasedText = translations['client.store.item.purchased'];
  }

  if (itemSectionType === VC_TYPE) {
    generatedData.viType = BUNDLE_TYPE;
    generatedData.requireAuth = true;
  }

  return generatedData;
};

export default async ({
  block,
  blockStructure,
  data,
  translations,
}: ScriptCommonArgs) => {
  const {
    isPreviewFrameMode,
    landingType,
    locale,
    auth,
    cart: cartSettings,
    isMultipage,
    isNewScheme,
  } = data;
  const enableCart = canShowCart({
    isMultipage,
    isNewScheme,
    enableCart: cartSettings.enable,
    placeType: PlaceTypes.STORE,
  });

  const store = getStore();
  const { landingServices } = store.getState();
  const {
    cart,
    storeAPIDataService,
    inventory,
    customAmountService,
    userService,
  } = landingServices;

  initCartModalOnce({ data, translations });

  const userToken = getTokenFromCookie(isPreviewFrameMode);
  const universalItemsSections = [];

  const initStore = async () => {
    const service = new StoreLoaderService();
    loader.attach(service);

    window.dispatchEvent(
      new CustomEvent(events.store.init, {
        detail: { blockId: block.id },
      })
    );

    [...block.getElementsByClassName('store__content')].forEach(
      (element, index) => {
        const { group: groupId, type } = element.dataset;
        if (
          ![GK_TYPE, BUNDLE_TYPE].includes(type) &&
          (!groupId || groupId === ERROR_VC_GROUP)
        ) {
          element.getElementsByClassName('store__preloader')[0].remove();
          renderEmptyTemplate(
            block.getElementsByClassName('store__section')[index],
            {
              isEmptyList: true,
              emptyListText: translations['client.store.no_items'],
            }
          );
          return;
        }
        universalItemsSections.push({
          id: groupId,
          // $FlowFixMe
          storeSectionId: element?.dataset?.storeSectionId,
          type,
          sectionIndex: index,
        });
      }
    );

    const uniqueVirtualItemsGroups = [
      ...new Set(
        universalItemsSections
          .filter(({ type }) => type === VI_TYPE)
          .map(({ id }) => id)
      ),
    ];

    const loadBundles =
      universalItemsSections.find(({ type }) => type === BUNDLE_TYPE) &&
      storeAPIDataService.loadBundles();
    const loadGameKeys =
      universalItemsSections.find(({ type }) => type === GK_TYPE) &&
      storeAPIDataService.loadGameKeys();
    const loadVirtualItems = Promise.all(
      uniqueVirtualItemsGroups.map(async (groupId) => ({
        groupId,
        data: await storeAPIDataService.loadVirtualItems(groupId),
      }))
    );

    const hasVCPacksOnPage = universalItemsSections.find(({ type }) =>
      [VC_TYPE, CUSTOM_AMOUNT].includes(type)
    );
    const loadVirtualCurrencyPacks =
      hasVCPacksOnPage && storeAPIDataService.loadVirtualCurrencyPacks();
    const loadVirtualCurrencies =
      hasVCPacksOnPage && storeAPIDataService.loadVirtualCurrencies();

    const resources = await Promise.all([
      loadBundles,
      loadGameKeys,
      loadVirtualItems,
      loadVirtualCurrencyPacks,
      loadVirtualCurrencies,
    ]);
    // $FlowFixMe
    if (resources.some((resource) => resource?.errorMessage)) {
      showNotification(translations['client.common_error'], 'error');
    }
    const [
      bundles = [],
      gameKeys = [],
      virtualItems = [],
      virtualCurrencyPacks = [],
      virtualCurrencies = [],
    ] = resources;

    window.dispatchEvent(
      new CustomEvent(events.store.dataLoaded, {
        detail: { blockId: block.id },
      })
    );
    const sections = block.querySelectorAll('.store__section');
    const sectionLists = block.querySelectorAll('.store__list');
    const sectionTitles = block.querySelectorAll('.store__section-title');
    const buttons = universalItemsSections.reduce(
      (allButtons, { id: groupId, sectionIndex, type: itemSectionType }) => {
        const currentSection = sections[sectionIndex];
        const currentSectionList = sectionLists[sectionIndex];
        const currentSectionTitle = sectionTitles[sectionIndex];
        let currentGroupItems: any = [];
        if (itemSectionType === BUNDLE_TYPE) {
          currentGroupItems = bundles;
        }
        if (itemSectionType === GK_TYPE) {
          currentGroupItems = gameKeys;
        }
        if (itemSectionType === VI_TYPE) {
          const foundVI = virtualItems.find((vi) => vi.groupId === groupId);
          currentGroupItems = foundVI && foundVI.data;
        }
        const hasVCPacksError =
          // $FlowFixMe
          !!virtualCurrencyPacks?.errorMessage ||
          // $FlowFixMe
          !!virtualCurrencies?.errorMessage;
        if (itemSectionType === VC_TYPE && !hasVCPacksError) {
          currentGroupItems =
            groupId === ALL_VC_TYPE_NAME
              ? virtualCurrencyPacks
              : virtualCurrencyPacks.filter(
                  (pack) => pack.content[0].sku === groupId
                );
        }

        if (itemSectionType === CUSTOM_AMOUNT && !hasVCPacksError) {
          currentGroupItems = virtualCurrencies.find(
            ({ sku }) => sku === groupId
          );
          if (!currentGroupItems) {
            return allButtons;
          }
          const serviceCustomAmountLoader = new CustomAmountLoaderService();
          loader.attach(serviceCustomAmountLoader);

          ReactDOM.render(
            <CustomAmountOnLanding
              merchantId={data.merchantId}
              locale={locale}
              item={currentGroupItems}
              blockId={block.id}
              onClickCustomAmount={onClickCustomAmount(customAmountService)}
            />,
            currentSectionList
          );
          currentSection.getElementsByClassName('store__preloader')[0].remove();
          loader.detach(serviceCustomAmountLoader);
        }

        if (groupId !== ALL_VC_TYPE_NAME) {
          const groupTitle =
            itemSectionType === VI_TYPE
              ? Array.isArray(currentGroupItems) &&
                currentGroupItems.length > 0 &&
                currentGroupItems[0].groups.find(
                  (element) => element.external_id === groupId.toString()
                )
              : virtualCurrencies.find(({ sku }) => sku === groupId);
          if (groupTitle && currentSectionTitle) {
            currentSectionTitle.innerText = groupTitle.name;
          }
        }
        if (currentGroupItems && Array.isArray(currentGroupItems)) {
          currentGroupItems = currentGroupItems.filter((item) =>
            item.canShow()
          );
        }

        const isEmptyItems =
          currentGroupItems &&
          (Array.isArray(currentGroupItems)
            ? currentGroupItems.length === 0
            : currentGroupItems.statusCode === 404);
        if (isEmptyItems) {
          currentSection.getElementsByClassName('store__preloader')[0].remove();
          renderEmptyTemplate(currentSection, {
            isEmptyList: true,
            emptyListText: translations['client.store.no_items'],
          });
          return allButtons;
        }

        let templateDataList = [];
        if (Array.isArray(currentGroupItems)) {
          templateDataList = currentGroupItems.map((item) => ({
            ...collectGeneratedData({
              item: ((item: any): Classed<AnyStoreItemType>),
              itemSectionType,
              translations,
              groupId,
              sectionIndex,
              blockStructure,
              landingType,
              enableCart,
              cart,
              storeAPIDataService,
              block,
              locale,
            }),
            cart,
            storeAPIDataService,
          }));
          generateTemplate({
            templateDataList,
            currentSection,
            currentSectionList,
            cart,
          });
          addListeners({ templateDataList, cart, inventory, userToken });
        }

        return [
          ...allButtons,
          ...templateDataList.reduce(
            (buttons, templateData) => [
              ...buttons,
              ...document.querySelectorAll(
                `#${templateData.buttonId}, #${templateData.buttonId}--modal`
              ),
            ],
            []
          ),
        ];
      },
      []
    );

    showMoreBundle();
    loader.detach(service);
    return buttons;
  };

  const buttons = await initStore();
  if (enableCart) {
    await cart.load();
  }
  if (userToken) {
    inventory.load();
  }

  const isAuth = !!(
    userToken ||
    auth.type === AuthorizationType.WEBHOOK ||
    auth.type === AuthorizationType.USER_ID
  );
  if (isAuth) {
    buyForRealPrice({
      buttons,
      data,
      block,
      translations,
      storeAPIDataService,
      isAuth,
    });
    buyForVirtualCurrency({ userToken, data, userService, translations });
  } else {
    documentClickListener.addListener({
      selector: '[data-require-auth="true"]',
      handle: (button: Element) => {
        analyticsManager.sendEvent({
          category: AnalyticsCategories.STORE,
          event: AnalyticsActions.CLICK_BUY,
          page: `landing_store_${AnalyticsActions.CLICK_BUY}`,
          label: button.getAttribute('data-sku'),
        });
        analyticsManager.sendEvent({
          category: AnalyticsCategories.STORE,
          event: AnalyticsActions.OPEN_LOGIN,
          page: `landing_${AnalyticsActions.OPEN_LOGIN}`,
        });
      },
    });
    const loginButtons = buttons.filter(
      (btn) => btn.getAttribute('data-require-auth') === 'true'
    );
    openLoginWidget(isCustomAuth(), loginButtons, {
      sourceFinder: (btn) => {
        const buyItem = btn.closest('[data-buy-button-item]');
        return buyItem ? buyItem.id : null;
      },
    });
    buyForRealPrice({
      buttons,
      block,
      isAuth,
      data,
      storeAPIDataService,
      translations,
    });
  }
};
